import { cva } from 'class-variance-authority';
import { useTranslation } from 'next-i18next';
import { MutableRefObject, useEffect, useRef } from 'react';

import { usePagination } from '@boss/hooks';
import { NewsArticleFields } from '@boss/types/corporate-jobs';
import { NewsArticleCard, Pagination } from '@boss/ui';

import { getNewsArticleProps } from '../../mappers/PropsMapping';

type Props = {
  newsArticles: NewsArticleFields[];
  pageTitle: string;
  locale: string;
};

const articleStyle = cva('', {
  variants: {
    highlighted: {
      true: 'md:col-span-3',
      false: 'col-span-1',
    },
  },
});

const NewsPage = ({ newsArticles, pageTitle, locale }: Props) => {
  const { t } = useTranslation(['news']);
  const { currentPage, paginate, currentItems, totalPages } = usePagination(7, newsArticles);
  const topRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

  useEffect(() => {
    if (topRef) {
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentPage]);

  return (
    <div className="scroll-mt-52" ref={topRef}>
      <div className="h1 my-12">{pageTitle}</div>
      {newsArticles.length > 0 ? (
        <>
          <div className="grid-col-1 grid gap-12 md:grid-cols-3">
            {currentItems?.map((newsArticle, index: number) => {
              return (
                <div className={articleStyle({ highlighted: index === 0 })} key={newsArticle.id}>
                  <NewsArticleCard
                    isHighlighted={index === 0}
                    translations={{
                      buttonText: t('cardButton'),
                    }}
                    {...getNewsArticleProps(newsArticle, locale)}
                  />
                </div>
              );
            })}
          </div>
          <div className="my-12 flex justify-center">
            <Pagination
              currentPage={currentPage}
              iconClassName="w-3 h-3"
              onPaginate={paginate}
              pageNumberClassName="border-r-1 border-l-1 border-solid border-gray-light"
              totalPages={totalPages}
            />
          </div>
        </>
      ) : (
        <p className="body">{t('noResult')}</p>
      )}
    </div>
  );
};

export default NewsPage;
